import type { CSSProperties } from 'react';

import { getReservedHeights } from '@lib/ads/snack-slots';
import type { SnackSlotId } from '@lib/ads/snack-slots';

interface SnackAdSlotProperties {
  slotId: SnackSlotId;
  /** Spacing around the slot, which only takes effect while it shows. */
  className?: string;
  /**
   * Whether the element the wrapper fills is there yet. The space is reserved
   * either way, so adding it later moves nothing.
   */
  isFillable?: boolean;
}

/**
 * An empty element for Snack's wrapper to fill with an ad (FFH-3481), in a box
 * that reserves the ad's height (getReservedHeights) so the page doesn't move
 * when one arrives.
 *
 * It renders for every visitor so that its space can be decided before first
 * paint: it stays hidden unless <html> carries AD_SPACE_ATTRIBUTE (see
 * styles/globals.css). Visitors who never load the wrapper only ever get an
 * empty, hidden element.
 */
const SnackAdSlot = ({
  slotId,
  className = '',
  isFillable = true
}: SnackAdSlotProperties) => {
  const { mobileInPx, desktopInPx } = getReservedHeights(slotId);

  return (
    <div
      className={`snack-ad-slot ${className}`}
      style={
        {
          '--snack-slot-mobile-height': `${mobileInPx}px`,
          '--snack-slot-desktop-height': `${desktopInPx}px`
        } as CSSProperties
      }
    >
      {isFillable && <div id={slotId} />}
    </div>
  );
};

export default SnackAdSlot;
